import { action, computed, makeObservable } from "mobx";
import type { ChangeEvent } from "react";
import { inject, injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";
import {
  REALTIME_STORE_TOKEN,
  type IRealtimeStore,
} from "../../stores/RealtimeStore";
import {
  DEFAULT_APP_SETTINGS,
  SETTINGS_STORE_TOKEN,
  type ISettingsStore,
} from "../../stores/SettingsStore";
import {
  BROWSER_NOTIFICATIONS_STORE_TOKEN,
  type IBrowserNotificationsStore,
} from "../../stores/BrowserNotificationsStore";

@injectable()
export class SettingsPageViewModel extends ViewModel {
  constructor(
    @inject(SETTINGS_STORE_TOKEN)
    private readonly settingsStore: ISettingsStore,
    @inject(REALTIME_STORE_TOKEN)
    private readonly realtimeStore: IRealtimeStore,
    @inject(BROWSER_NOTIFICATIONS_STORE_TOKEN)
    private readonly browserNotificationsStore: IBrowserNotificationsStore,
  ) {
    super();
    makeObservable(this);
  }

  @computed
  get realtimeEnabled(): boolean {
    return this.settingsStore.realtimeEnabled;
  }

  @computed
  get showSystemEventsInDashboard(): boolean {
    return this.settingsStore.showSystemEventsInDashboard;
  }

  @computed
  get browserNotificationsEnabled(): boolean {
    return this.settingsStore.browserNotificationsEnabled;
  }

  @computed
  get webPushEnabled(): boolean {
    return this.settingsStore.webPushEnabled;
  }

  @computed
  get takenItemsRemindersEnabled(): boolean {
    return this.settingsStore.takenItemsRemindersEnabled;
  }

  @computed
  get aiPhotoAutofillEnabled(): boolean {
    return this.settingsStore.aiPhotoAutofillEnabled;
  }

  @computed
  get isNotificationApiSupported(): boolean {
    return this.browserNotificationsStore.isSupported;
  }

  @computed
  get notificationPermissionLabel(): string {
    return this.browserNotificationsStore.permissionLabel;
  }

  @computed
  get isWebPushSupported(): boolean {
    return this.browserNotificationsStore.isWebPushSupported;
  }

  @computed
  get webPushStatusLabel(): string {
    return this.browserNotificationsStore.webPushStatusLabel;
  }

  @computed
  get canSendWebPushTest(): boolean {
    return this.browserNotificationsStore.canSendTestWebPush;
  }

  @computed
  get canRequestNotificationPermission(): boolean {
    return this.browserNotificationsStore.canRequestPermission;
  }

  @computed
  get notificationsError(): string | null {
    return this.browserNotificationsStore.lastError;
  }

  @computed
  get notificationsInfo(): string | null {
    return this.browserNotificationsStore.infoMessage;
  }

  @computed
  get statusLabel(): string {
    return this.realtimeStore.statusLabel;
  }

  @computed
  get reconnectAttemptsTotal(): number {
    return this.realtimeStore.reconnectAttemptsTotal;
  }

  @computed
  get reconnectStormCount(): number {
    return this.realtimeStore.reconnectStormCount;
  }

  @computed
  get canReset(): boolean {
    return (
      this.settingsStore.realtimeEnabled !==
        DEFAULT_APP_SETTINGS.realtimeEnabled ||
      this.settingsStore.showSystemEventsInDashboard !==
        DEFAULT_APP_SETTINGS.showSystemEventsInDashboard ||
      this.settingsStore.browserNotificationsEnabled !==
        DEFAULT_APP_SETTINGS.browserNotificationsEnabled ||
      this.settingsStore.webPushEnabled !==
        DEFAULT_APP_SETTINGS.webPushEnabled ||
      this.settingsStore.takenItemsRemindersEnabled !==
        DEFAULT_APP_SETTINGS.takenItemsRemindersEnabled ||
      this.settingsStore.aiPhotoAutofillEnabled !==
        DEFAULT_APP_SETTINGS.aiPhotoAutofillEnabled
    );
  }

  @action.bound
  onRealtimeEnabledChange(event: ChangeEvent<HTMLInputElement>): void {
    this.settingsStore.setRealtimeEnabled(event.target.checked);
  }

  @action.bound
  onShowSystemEventsChange(event: ChangeEvent<HTMLInputElement>): void {
    this.settingsStore.setShowSystemEventsInDashboard(event.target.checked);
  }

  @action.bound
  onBrowserNotificationsEnabledChange(
    event: ChangeEvent<HTMLInputElement>,
  ): void {
    this.settingsStore.setBrowserNotificationsEnabled(event.target.checked);
  }

  @action.bound
  onWebPushEnabledChange(event: ChangeEvent<HTMLInputElement>): void {
    this.settingsStore.setWebPushEnabled(event.target.checked);
  }

  @action.bound
  onTakenItemsRemindersEnabledChange(
    event: ChangeEvent<HTMLInputElement>,
  ): void {
    this.settingsStore.setTakenItemsRemindersEnabled(event.target.checked);
  }

  @action.bound
  onAiPhotoAutofillEnabledChange(event: ChangeEvent<HTMLInputElement>): void {
    this.settingsStore.setAiPhotoAutofillEnabled(event.target.checked);
  }

  @action.bound
  requestNotificationPermission(): void {
    void this.browserNotificationsStore.requestPermission();
  }

  @action.bound
  sendWebPushTest(): void {
    void this.browserNotificationsStore.sendTestWebPush();
  }

  @action.bound
  resetSettings(): void {
    this.settingsStore.reset();
  }
}
